/**
 * Fase 6 (2026-07-01) — Panel de tandas para un pedido segmentado.
 *
 * Se renderiza SOLO cuando la operación del pedido tiene
 * flag_pedido_segmentado=1. Muestra las tandas DÍA y NOCHE como
 * dos bloques independientes con sus propios botones:
 *   • Iniciar tanda   (pendiente/solicitado → en_preparacion)
 *   • Terminar tanda  (en_preparacion → terminado / pse: listo)
 *   • Enviar despacho (terminado → entregado_a_despacho)
 *
 * La entrega al cliente vive en la pantalla de Despacho para respetar
 * la separación de roles cocina/despacho. Aquí solo cocina.
 *
 * Guard de stock: si el backend responde 409 al iniciar, se levanta
 * un toast con la lista de ingredientes faltantes por nombre.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Sun,
  Moon,
  Boxes,
  Loader2,
  Play,
  Check,
  Truck,
  AlertTriangle,
  X,
} from 'lucide-react';
import {
  cocinaTandaService,
  type Segmento,
  type TandaSnapshot,
  type IngredienteFaltanteMsg,
} from '../../services/cocinaTandaService';
import { useToast } from '../../hooks/useToast';

type AccionTanda = 'iniciar' | 'terminar' | 'enviar-a-despacho';

export type FiltroSegmento = 'todos' | 'dia' | 'noche';

interface Props {
  pedidoId: number;
  pedidoCodigo?: string | null;
  /** Opcional: callback tras acción exitosa para refrescar la lista padre. */
  onTandaCambio?: () => void;
  /** Filtro de segmento aplicado desde la vista padre. */
  filtroSegmento?: FiltroSegmento;
  /** Callback con el snapshot actual de tandas — el padre lo usa
   * para calcular el estado efectivo del pedido y mostrar chips por
   * segmento coherentes con la realidad, no con el estado global. */
  onTandasSnapshot?: (tandas: TandaSnapshot[]) => void;
}

const SEG_META: Record<
  Segmento,
  { label: string; icon: React.ReactNode; grad: string; ring: string }
> = {
  dia: {
    label: 'DÍA · Almuerzo',
    icon: <Sun className="w-5 h-5" />,
    grad: 'from-amber-100 to-amber-50 dark:from-amber-900/40 dark:to-amber-800/20',
    ring: 'ring-amber-300 dark:ring-amber-700',
  },
  noche: {
    label: 'NOCHE · Cena',
    icon: <Moon className="w-5 h-5" />,
    grad: 'from-indigo-100 to-indigo-50 dark:from-indigo-900/40 dark:to-indigo-800/20',
    ring: 'ring-indigo-300 dark:ring-indigo-700',
  },
  transversal: {
    label: 'TRANSVERSAL',
    icon: <Boxes className="w-5 h-5" />,
    grad: 'from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-700',
    ring: 'ring-slate-300 dark:ring-slate-600',
  },
};

const ESTADO_LABEL: Record<string, { text: string; color: string }> = {
  solicitado: {
    text: 'Solicitado',
    color: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  },
  en_preparacion: {
    text: 'En preparación',
    color: 'bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-100',
  },
  listo: {
    text: 'Listo',
    color:
      'bg-emerald-200 text-emerald-900 dark:bg-emerald-700 dark:text-emerald-100',
  },
  entregado_a_despacho: {
    text: 'En despacho',
    color: 'bg-blue-200 text-blue-900 dark:bg-blue-700 dark:text-blue-100',
  },
  despachado: {
    text: 'Despachado',
    color:
      'bg-emerald-500 text-white dark:bg-emerald-600 dark:text-white',
  },
  cancelado: {
    text: 'Cancelado',
    color: 'bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-100',
  },
};

/** Acción disponible según estado FSM del segmento. */
function accionSiguiente(estado: string | undefined | null): AccionTanda | null {
  if (!estado) return 'iniciar';
  switch (estado) {
    case 'solicitado':
      return 'iniciar';
    case 'en_preparacion':
      return 'terminar';
    case 'listo':
      return 'enviar-a-despacho';
    default:
      return null;
  }
}

function tituloAccion(a: AccionTanda): {
  label: string;
  icon: React.ReactNode;
  style: string;
} {
  switch (a) {
    case 'iniciar':
      return {
        label: 'Iniciar tanda',
        icon: <Play className="w-4 h-4" />,
        style:
          'bg-amber-500 hover:bg-amber-600 text-white shadow shadow-amber-500/20',
      };
    case 'terminar':
      return {
        label: 'Terminar tanda',
        icon: <Check className="w-4 h-4" />,
        style:
          'bg-emerald-600 hover:bg-emerald-700 text-white shadow shadow-emerald-600/20',
      };
    case 'enviar-a-despacho':
      return {
        label: 'Enviar a despacho',
        icon: <Truck className="w-4 h-4" />,
        style:
          'bg-blue-600 hover:bg-blue-700 text-white shadow shadow-blue-600/20',
      };
  }
}

const PanelTandasPedido: React.FC<Props> = ({
  pedidoId,
  pedidoCodigo,
  onTandaCambio,
  filtroSegmento = 'todos',
  onTandasSnapshot,
}) => {
  const [tandas, setTandas] = useState<TandaSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [actuando, setActuando] = useState<Segmento | null>(null);
  const [stockFaltante, setStockFaltante] = useState<
    Partial<Record<Segmento, IngredienteFaltanteMsg[]>>
  >({});
  const { success, error: toastError } = useToast();

  const refrescar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cocinaTandaService.listarTandas(pedidoId);
      setTandas(data);
      onTandasSnapshot?.(data);
    } catch (err) {
      console.error('Error listar tandas', err);
    } finally {
      setLoading(false);
    }
  }, [pedidoId, onTandasSnapshot]);

  useEffect(() => {
    refrescar();
  }, [refrescar]);

  const ejecutar = async (segmento: Segmento, accion: AccionTanda) => {
    setActuando(segmento);
    try {
      let resultado;
      switch (accion) {
        case 'iniciar':
          resultado = await cocinaTandaService.iniciar(pedidoId, segmento);
          success(
            `Tanda ${SEG_META[segmento].label} iniciada`,
            `${resultado.registros_iniciados ?? 0} registros`
          );
          break;
        case 'terminar':
          resultado = await cocinaTandaService.terminar(pedidoId, segmento);
          success(
            `Tanda ${SEG_META[segmento].label} terminada`,
            `${resultado.registros_transicionados ?? 0} registros`
          );
          break;
        case 'enviar-a-despacho':
          resultado = await cocinaTandaService.enviarADespacho(
            pedidoId,
            segmento
          );
          success(
            `Tanda ${SEG_META[segmento].label} enviada a despacho`,
            `${resultado.registros_transicionados ?? 0} registros`
          );
          break;
      }
      // Éxito → limpiar cualquier faltante previo de este segmento.
      setStockFaltante((prev) => {
        if (!prev[segmento]) return prev;
        const nuevo = { ...prev };
        delete nuevo[segmento];
        return nuevo;
      });
      await refrescar();
      onTandaCambio?.();
    } catch (err) {
      const anyErr = err as {
        response?: {
          status?: number;
          data?: { error?: string; detalles?: TandaError409Detalles };
        };
      };
      if (
        anyErr?.response?.status === 409 &&
        anyErr.response.data?.detalles?.ingredientes_faltantes
      ) {
        const faltantes = anyErr.response.data.detalles
          .ingredientes_faltantes as IngredienteFaltanteMsg[];
        // Persistir el faltante en un panel dentro de la card
        // (UX-2: el toast se disipa en ~2s, el panel queda hasta
        // que el usuario reintente o lo cierre manualmente).
        setStockFaltante((prev) => ({ ...prev, [segmento]: faltantes }));
        const listado = faltantes
          .slice(0, 5)
          .map(
            (f) =>
              `${f.nombre}: hay ${f.stock_disponible} ${f.unidad}, requiere ${f.cantidad_necesaria}`
          )
          .join(' · ');
        const extra =
          faltantes.length > 5 ? ` (+${faltantes.length - 5} más)` : '';
        toastError(
          `Stock insuficiente en ${SEG_META[segmento].label}`,
          listado + extra
        );
      } else {
        toastError(
          'No se pudo actualizar la tanda',
          anyErr?.response?.data?.error ?? 'Error de red o backend'
        );
      }
    } finally {
      setActuando(null);
    }
  };

  const cerrarFaltante = (segmento: Segmento) => {
    setStockFaltante((prev) => {
      if (!prev[segmento]) return prev;
      const nuevo = { ...prev };
      delete nuevo[segmento];
      return nuevo;
    });
  };

  interface TandaError409Detalles {
    ingredientes_faltantes: IngredienteFaltanteMsg[];
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando tandas…
      </div>
    );
  }

  if (tandas.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic py-6 text-center border border-dashed rounded-lg">
        Aún no hay tandas creadas para este pedido. Pasa el pedido a “En
        preparación” para generar las tandas.
      </div>
    );
  }

  const tandasOperativas = tandas
    .filter((t) => t.segmento !== 'transversal')
    .filter((t) =>
      filtroSegmento === 'todos' ? true : t.segmento === filtroSegmento
    );
  // Transversal se oculta cuando hay un filtro de servicio activo
  // (bebidas no pertenecen ni a día ni a noche).
  const transversal =
    filtroSegmento === 'todos'
      ? tandas.find((t) => t.segmento === 'transversal')
      : null;

  return (
    <div className="space-y-4" data-testid={`panel-tandas-pedido-${pedidoId}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Tandas segmentadas
          {pedidoCodigo ? (
            <span className="text-sm font-normal text-gray-500 ml-2">
              · {pedidoCodigo}
            </span>
          ) : null}
        </h3>
        <button
          type="button"
          onClick={refrescar}
          className="text-xs text-blue-600 hover:underline"
        >
          Refrescar
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tandasOperativas.map((t) => {
          const meta = SEG_META[t.segmento];
          const estadoLabel = ESTADO_LABEL[t.estado_seg] ?? {
            text: t.estado_seg,
            color: 'bg-gray-200 text-gray-700',
          };
          const accion = accionSiguiente(t.estado_seg);
          return (
            <div
              key={t.segmento}
              className={`bg-gradient-to-br ${meta.grad} rounded-xl border ring-1 ${meta.ring} p-5 flex flex-col gap-3`}
              data-testid={`tanda-${t.segmento}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-100">
                  {meta.icon}
                  {meta.label}
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${estadoLabel.color}`}
                  data-testid={`tanda-${t.segmento}-estado`}
                >
                  {estadoLabel.text}
                </span>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-200">
                <span className="font-medium">{t.n_registros}</span> registro
                {t.n_registros === 1 ? '' : 's'} en cocina
                {t.estados_cr ? (
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">
                    ({t.estados_cr})
                  </span>
                ) : null}
              </div>
              {stockFaltante[t.segmento] ? (
                <div
                  className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50/95 dark:bg-red-950/60 p-3 text-xs text-red-900 dark:text-red-100 shadow-sm"
                  data-testid={`tanda-${t.segmento}-stock-faltante`}
                  role="alert"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 font-semibold leading-tight">
                      Stock insuficiente — no se puede iniciar esta tanda.
                    </div>
                    <button
                      type="button"
                      onClick={() => cerrarFaltante(t.segmento)}
                      className="p-0.5 rounded hover:bg-red-200/60 dark:hover:bg-red-800/40 transition"
                      aria-label="Cerrar aviso de stock"
                      data-testid={`tanda-${t.segmento}-stock-cerrar`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ul className="space-y-0.5 max-h-32 overflow-auto pl-6 list-disc">
                    {stockFaltante[t.segmento]!.map((f) => (
                      <li key={f.ingrediente_id}>
                        <span className="font-medium">{f.nombre}</span>: hay{' '}
                        {f.stock_disponible} {f.unidad}, requiere{' '}
                        {f.cantidad_necesaria} (faltan{' '}
                        <span className="font-semibold">
                          {f.faltante} {f.unidad}
                        </span>
                        )
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 text-[11px] italic text-red-700 dark:text-red-200">
                    Repone stock o ajusta el pedido y vuelve a iniciar.
                  </div>
                </div>
              ) : null}
              <div className="mt-auto">
                {accion ? (
                  <button
                    type="button"
                    onClick={() => ejecutar(t.segmento, accion)}
                    disabled={actuando === t.segmento}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 transition ${
                      tituloAccion(accion).style
                    }`}
                    data-testid={`tanda-${t.segmento}-accion`}
                  >
                    {actuando === t.segmento ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      tituloAccion(accion).icon
                    )}
                    {tituloAccion(accion).label}
                  </button>
                ) : (
                  <div className="text-center text-xs text-gray-500 italic py-2">
                    {t.estado_seg === 'entregado_a_despacho'
                      ? 'Esperando entrega al cliente en Despacho'
                      : t.estado_seg === 'despachado'
                      ? 'Tanda entregada al cliente'
                      : t.estado_seg}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {tandasOperativas.length === 0 ? (
          <div
            className="col-span-1 md:col-span-2 text-center text-xs text-gray-500 italic py-6 border border-dashed rounded-lg"
            data-testid="tandas-vacio-por-filtro"
          >
            Sin tandas en el filtro seleccionado.
          </div>
        ) : null}
      </div>
      {transversal && transversal.n_registros > 0 ? (
        <div
          className={`bg-gradient-to-br ${SEG_META.transversal.grad} rounded-xl border ring-1 ${SEG_META.transversal.ring} p-4`}
          data-testid="tanda-transversal"
        >
          <div className="flex items-center gap-2 text-sm">
            {SEG_META.transversal.icon}
            <span className="font-medium">Transversal · Bebidas</span>
            <span className="text-xs text-gray-500">
              ({transversal.n_registros} registro
              {transversal.n_registros === 1 ? '' : 's'})
            </span>
            <span
              className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                ESTADO_LABEL[transversal.estado_seg]?.color ?? ''
              }`}
            >
              {ESTADO_LABEL[transversal.estado_seg]?.text ?? transversal.estado_seg}
            </span>
          </div>
          <p
            className="mt-2 text-xs italic text-slate-600 dark:text-slate-400 leading-snug"
            data-testid="transversal-leyenda"
          >
            Las bebidas no requieren preparación en cocina — se gestionan
            directamente en Despacho al armar cada tanda (día y noche).
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default PanelTandasPedido;
